import { Link } from '@mui/material';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { DigestAlgorithm } from '../../../common/certificates/enums/DigestAlgorithm';
import { Validity } from '../../../common/certificates/enums/Validity';
import { ValidityLength } from '../../../common/certificates/enums/ValidityLength';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { AuthorizationError } from '../../../common/errors/errors';
import { KEY_LENGTH_OPTIONS, KEY_LENGTH_OPTION_VALIDATOR } from '../../../common/keys/constants/KeyLengths';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { DateUtils } from '../../../common/utils/DateUtils';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { OrganizationCertificate } from '../models/OrganizationCertificate';
import { OrganizationCertificatesService } from '../services/OrganizationCertificatesService';

type State = PaginatedTableState & {};

type Props = {};

export class OrganizationCertificatesController extends PaginatedTableController<OrganizationCertificate, Props, State> {
  private readonly certificateService = new OrganizationCertificatesService();

  componentWillUnmount() {
    super.componentWillUnmount();

    this.certificateService.cancelAllRequests();
  }

  protected doInitialDataFetch(): void {
    // do not fetch until permissions are checked
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canAccessOrganizationCertificate();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    super.doInitialDataFetch();
  }

  protected renderTable(tableComponent: ReactElement) {
    return (
      <SecondaryNav title={t(TabNameLocalizationKeys.CERTIFICATES)} subtitle={t('certificatesListSubtitle')} getBreadcrumbs={this.getBreadcrumbs}>
        {tableComponent}
      </SecondaryNav>
    );
  }

  protected getFieldConfigurations(): FieldConfiguration<OrganizationCertificate>[] {
    return [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: {
          maxWidth: 100,
        },
        render: (item, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.CERTIFICATE, { certificateId: item.getId().toString() })}
            aria-label={t('a11y.organization.certificates.certificateIdLabel', { certId: item.getId() }) as string}
          >
            {item.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'subject',
        label: t('subject'),
        description: t('subject'),
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getSubject()}</TypographyOverflow>,
      },
      {
        fieldKey: 'issuer',
        label: t('issuer'),
        description: t('issuer'),
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getIssuer()}</TypographyOverflow>,
      },
      {
        fieldKey: 'expiryDate',
        label: t('expiryDate'),
        description: t('expiryDate'),
        render: (item, { hasFocus }) => {
          const date = item.getExpiryDate();
          if (date) {
            return <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toDateTimeString(date)}</TypographyOverflow>;
          }
          return <BlankCell />;
        },
      },
      {
        fieldKey: 'digestAlgorithm',
        label: t('digestAlgorithm'),
        description: t('digestAlgorithm'),
        render: (item, { hasFocus }) => {
          const algorithm = item.getDigestAlgorithm();
          if (algorithm) {
            return <TypographyOverflow hasFocus={hasFocus}>{algorithm}</TypographyOverflow>;
          }
          return <BlankCell />;
        },
      },
      {
        fieldKey: 'publicKey',
        label: t('publicKey'),
        description: t('publicKey'),
        render: (item, { tabIndex, hasFocus }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.KEY, { keyId: item.getPublicKey().getId().toString() })}
            aria-label={t('a11y.organization.certificates.publicKeyLabel', { keyId: item.getPublicKey().getId() }) as string}
          >
            <TypographyOverflow hasFocus={hasFocus}>{item.getPublicKey().getShortDescription()}</TypographyOverflow>
          </Link>
        ),
      },
      {
        fieldKey: 'fingerprint',
        label: t('fingerprint'),
        description: t('fingerprint'),
        render: (item, { hasFocus }) => {
          const fingerprint = item.getFingerprint();
          if (fingerprint) {
            return <TypographyOverflow hasFocus={hasFocus}>{fingerprint}</TypographyOverflow>;
          }
          return <BlankCell />;
        },
      },
      {
        fieldKey: 'matchedCertificates',
        label: t('matchedCertificates'),
        description: t('matchedCertificates'),
        render: (item) => item.getMatchedCertificatesCount(),
      },
    ];
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'isSelfSigned',
        label: t('selfSigned'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: [
          { value: 'TRUE', label: t('true') },
          { value: 'FALSE', label: t('false') },
        ],
      },
      {
        filterKey: 'isCa',
        label: t('caCertificate'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: [
          { value: 'TRUE', label: t('true') },
          { value: 'FALSE', label: t('false') },
        ],
      },
      {
        filterKey: 'digestAlgorithm',
        label: t('digestAlgorithm'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(DigestAlgorithm).map((value) => ({ value, label: value.replace('_', '') })),
      },
      {
        filterKey: 'validityLength',
        label: t('validityLength'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(ValidityLength).map((value) => ({ value, label: t(`validityLengths.${value}`) })),
      },
      {
        filterKey: 'validity',
        label: t('validity'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: Object.values(Validity).map((value) => ({ value, label: t(`validities.${value}`) })),
      },
      {
        filterKey: 'keyLength',
        label: t('keyLength'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.GE, label: t('filterLabels.isGreaterOrEqual') },
          { value: Operation.GT, label: t('filterLabels.isGreater') },
          { value: Operation.LE, label: t('filterLabels.isLessOrEqual') },
          { value: Operation.LT, label: t('filterLabels.isLess') },
        ],
        options: KEY_LENGTH_OPTIONS(),
        validator: KEY_LENGTH_OPTION_VALIDATOR,
      },
    ];
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<OrganizationCertificate>> {
    try {
      return await this.certificateService.getList(cursorInfo, this.getFilters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getCertificatesBreadcrumbs();
  }
}
